import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { BOOKING_STATUS, RESOURCE_TYPES, fmtDateTimeTH } from './aoShared'

const emptyForm = { rs_id: '', bk_start: '', bk_end: '', bk_purpose: '', bk_destination: '' }

// จองห้องประชุม/รถโรงเรียน — ครูทุกคน พร้อมตรวจเวลาว่างก่อนส่ง
export default function MyBooking() {
  const [bookings, setBookings] = useState([])
  const [resources, setResources] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState('')
  const [conflict, setConflict] = useState(null) // null = ยังไม่เช็ค, false = ว่าง, object = ชน

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [bkRes, rsRes] = await Promise.all([
        axiosInstance.get('/api/ao/my/bookings'),
        axiosInstance.get('/api/ao/resources'),
      ])
      if (bkRes.data.success) setBookings(bkRes.data.data)
      if (rsRes.data.success) setResources(rsRes.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const selectedResource = useMemo(
    () => resources.find(r => String(r.rs_id) === String(form.rs_id)),
    [resources, form.rs_id])

  // live conflict check เมื่อเลือกทรัพยากร+ช่วงเวลาครบ
  useEffect(() => {
    setConflict(null)
    if (!showModal || !form.rs_id || !form.bk_start || !form.bk_end || form.bk_end <= form.bk_start) return
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ rs_id: form.rs_id, start: form.bk_start, end: form.bk_end })
        const res = await axiosInstance.get(`/api/ao/bookings/check-conflict?${params.toString()}`)
        if (res.data.success) setConflict(res.data.data.conflict || false)
      } catch { /* เช็คไม่ได้ก็ให้ server ตัดสินตอน submit */ }
    }, 400)
    return () => clearTimeout(timer)
  }, [showModal, form.rs_id, form.bk_start, form.bk_end])

  const openModal = () => {
    setForm(emptyForm)
    setConflict(null)
    setModalError('')
    setShowModal(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (form.bk_end <= form.bk_start) {
      setModalError('เวลาสิ้นสุดต้องอยู่หลังเวลาเริ่มต้น')
      return
    }
    setSaving(true)
    setModalError('')
    try {
      const res = await axiosInstance.post('/api/ao/bookings', form)
      if (res.data.success) {
        setShowModal(false)
        Swal.fire({ icon: 'success', title: 'ส่งคำขอจองเรียบร้อย', text: 'รอการอนุมัติจากงานธุรการ', timer: 2000, showConfirmButton: false })
        fetchData()
      }
    } catch (err) {
      setModalError(err.response?.data?.message || 'ส่งคำขอไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = async (b) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'ยกเลิกการจอง?',
      text: `${b.rs_name} · ${fmtDateTimeTH(b.bk_start)}`,
      showCancelButton: true,
      confirmButtonText: 'ยกเลิกการจอง',
      cancelButtonText: 'ปิด',
      confirmButtonColor: '#dc3545',
    })
    if (!result.isConfirmed) return
    try {
      await axiosInstance.put(`/api/ao/my/bookings/${b.bk_id}/cancel`)
      fetchData()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'ยกเลิกไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
            <h4 className="main-title mb-0">
              <i className="ri-calendar-check-line me-2 text-primary" />
              จองห้องประชุม / รถโรงเรียน
            </h4>
            <div className="d-flex gap-2">
              <Button as={Link} to="/booking-calendar" variant="outline-primary" size="sm">
                <i className="ri-calendar-line me-1" />ปฏิทินการจอง
              </Button>
              <Button variant="primary" size="sm" onClick={openModal}>
                <i className="ri-add-line me-1" />จองใหม่
              </Button>
            </div>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : (
            <Card className="card-one">
              <Card.Header><Card.Title as="h6" className="mb-0">การจองของฉัน</Card.Title></Card.Header>
              <Card.Body className="p-0">
                {bookings.length === 0 ? (
                  <div className="text-center text-muted py-4">ยังไม่มีการจอง</div>
                ) : (
                  <Table responsive hover className="mb-0 fs-sm">
                    <thead className="table-light">
                      <tr>
                        <th>ทรัพยากร</th>
                        <th>เริ่ม</th>
                        <th>สิ้นสุด</th>
                        <th>วัตถุประสงค์</th>
                        <th style={{ width: 130 }}>สถานะ</th>
                        <th style={{ width: 90 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map(b => {
                        const st = BOOKING_STATUS[b.bk_status] || BOOKING_STATUS.pending
                        const rt = RESOURCE_TYPES[b.rs_type] || RESOURCE_TYPES.room
                        return (
                          <tr key={b.bk_id}>
                            <td><Badge bg={rt.bg} className="me-1">{rt.label}</Badge>{b.rs_name}</td>
                            <td>{fmtDateTimeTH(b.bk_start)}</td>
                            <td>{fmtDateTimeTH(b.bk_end)}</td>
                            <td className="text-truncate" style={{ maxWidth: 220 }} title={b.bk_purpose}>
                              {b.bk_purpose}
                              {b.bk_destination && <div className="text-muted" style={{ fontSize: '0.75rem' }}>ปลายทาง: {b.bk_destination}</div>}
                            </td>
                            <td>
                              <Badge bg={st.bg}>{st.label}</Badge>
                              {b.approve_note && <div className="text-muted" style={{ fontSize: '0.75rem' }}>{b.approve_note}</div>}
                            </td>
                            <td className="text-end">
                              {['pending', 'approved'].includes(b.bk_status) && (
                                <Button size="sm" variant="outline-danger" onClick={() => handleCancel(b)}>ยกเลิก</Button>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          )}
        </Container>
      </div>
      <Footer />

      {/* Booking modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">จองห้องประชุม / รถโรงเรียน</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {modalError && <Alert variant="danger" className="py-2">{modalError}</Alert>}
            <Form.Group className="mb-2">
              <Form.Label className="fs-sm mb-1">ทรัพยากร <span className="text-danger">*</span></Form.Label>
              <Form.Select size="sm" required value={form.rs_id}
                onChange={e => setForm(f => ({ ...f, rs_id: e.target.value }))}>
                <option value="">— เลือกห้อง/รถ —</option>
                {resources.map(r => (
                  <option key={r.rs_id} value={r.rs_id}>
                    [{RESOURCE_TYPES[r.rs_type]?.label || r.rs_type}] {r.rs_name}{r.rs_detail ? ` (${r.rs_detail})` : ''}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Row className="g-2 mb-2">
              <Col xs={6}>
                <Form.Label className="fs-sm mb-1">เริ่ม <span className="text-danger">*</span></Form.Label>
                <Form.Control size="sm" type="datetime-local" required value={form.bk_start}
                  onChange={e => setForm(f => ({ ...f, bk_start: e.target.value }))} />
              </Col>
              <Col xs={6}>
                <Form.Label className="fs-sm mb-1">สิ้นสุด <span className="text-danger">*</span></Form.Label>
                <Form.Control size="sm" type="datetime-local" required min={form.bk_start} value={form.bk_end}
                  onChange={e => setForm(f => ({ ...f, bk_end: e.target.value }))} />
              </Col>
            </Row>
            {conflict === false && (
              <Alert variant="success" className="py-1 fs-sm mb-2"><i className="ri-check-line me-1" />ช่วงเวลานี้ว่าง</Alert>
            )}
            {conflict && conflict !== false && (
              <Alert variant="danger" className="py-1 fs-sm mb-2">
                <i className="ri-error-warning-line me-1" />
                ช่วงเวลานี้ถูกจองแล้วโดย {conflict.booked_by} ({fmtDateTimeTH(conflict.bk_start)} - {fmtDateTimeTH(conflict.bk_end)})
              </Alert>
            )}
            <Form.Group className="mb-2">
              <Form.Label className="fs-sm mb-1">วัตถุประสงค์ <span className="text-danger">*</span></Form.Label>
              <Form.Control size="sm" type="text" maxLength={500} required value={form.bk_purpose}
                onChange={e => setForm(f => ({ ...f, bk_purpose: e.target.value }))} />
            </Form.Group>
            {selectedResource?.rs_type === 'vehicle' && (
              <Form.Group>
                <Form.Label className="fs-sm mb-1">ปลายทาง</Form.Label>
                <Form.Control size="sm" type="text" maxLength={255} value={form.bk_destination}
                  onChange={e => setForm(f => ({ ...f, bk_destination: e.target.value }))} />
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>ยกเลิก</Button>
            <Button variant="primary" size="sm" type="submit" disabled={saving || (conflict && conflict !== false)}>
              {saving ? <Spinner size="sm" className="me-1" /> : <i className="ri-send-plane-line me-1" />}
              ส่งคำขอจอง
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}
